import { ComponentProps, useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';

import { toJS } from 'mobx';
import { SelectInput, MultiSelectInput } from '../forms';
import { OrderStore, orderActions } from '../../../orders/store/orders/orderStore';
import { TyreStorageOrder } from '../../../orders/api/orderApi';

type TyreStorageOrdersSelectorProps = Omit<ComponentProps<typeof SelectInput | typeof MultiSelectInput>, 'value' | 'defaultValue'> & {
    orderStore?: OrderStore,
    orderId: string,
    clientId: string,
    multiSelect?: boolean,
    notActive?: boolean,
  };

// Разноширокий комплект: на осях шины разных размеров. В заявке хранения (сервис 69)
// вторая ось заполняется только при галке «Разноширокие» — по ней и понимаем,
// что комплект разноширокий
const isStaggeredKit = (order: TyreStorageOrder): boolean => {
  const { specificRefValues: refs, specificValues: values } = order;
  if (!refs?.axle2TireWidthRefValueId && !refs?.axle2TireHeightRefValueId) { return false; }
  return (refs.axle1TireWidthRefValueId !== refs.axle2TireWidthRefValueId)
    || (refs.axle1TireHeightRefValueId !== refs.axle2TireHeightRefValueId)
    || (values?.axle1TireBrandId !== values?.axle2TireBrandId)
    || (values?.axle1TireModelId !== values?.axle2TireModelId);
};

const axleKitTitle = (
  brand?: string,
  model?: string,
  width?: string,
  height?: string,
  tireCount?: number
): string => `${ brand ?? '' } ${ model ?? '' } ${ width ?? '' }/${ height ?? '' } x ${ tireCount ?? '' }`;

const tyreStorageOrderOptionName = (v: TyreStorageOrder): string => `${
  v.number } от ${
  // TODO - возможно, надо конвертить даты в локальный часовой пояс офиса
  // пока не делал - не понятно, надо ли оно вообще тут
  // v?.createdDate ? moment.unix(v?.createdDate).tz('Europe/Moscow').format('DD.MM.YYYY HH:mm') : ''
  v.createdDate.substring(0, 10) ?? '' } ${
  isStaggeredKit(v)
    ? `разноширокие ${
      v.diameterWheelRefValueName ?? '' } — ось 1: ${
      axleKitTitle(
        v.specificValues?.axle1TireBrandName,
        v.specificValues?.axle1TireModelName,
        v.specificRefValues?.axle1TireWidthRefValueName,
        v.specificRefValues?.axle1TireHeightRefValueName,
        v.specificValues?.axle1TireCount
      ) }, ось 2: ${
      axleKitTitle(
        v.specificValues?.axle2TireBrandName,
        v.specificValues?.axle2TireModelName,
        v.specificRefValues?.axle2TireWidthRefValueName,
        v.specificRefValues?.axle2TireHeightRefValueName,
        v.specificValues?.axle2TireCount
      ) } — ${
      v.vehicleMakeTitle ?? '' } ${
      v.vehicleModelTitle ?? '' }`
    : `${
      v.specificValues?.axle1TireBrandName ?? '' } ${
      v.specificValues?.axle1TireModelName ?? '' } ${
      v.specificRefValues?.axle1TireWidthRefValueName ?? '' }/${
      v.specificRefValues?.axle1TireHeightRefValueName ?? '' }/${
      v.diameterWheelRefValueName ?? '' } ${
      v.vehicleMakeTitle ?? '' } ${
      v.vehicleModelTitle ?? '' } x ${
      v.tireCount ?? '' }`
} (${ v.statusRefValueName })`;

const TyreStorageOrdersSelector = (props:TyreStorageOrdersSelectorProps) => {
  const {
    orderStore,
    orderId,
    clientId,
    name = 'tyreStorageOrderId',
    multiSelect = false,
    notActive,
    label,
    placeholder,
    ...rest
  } = props;

  const [loading, setLoading] = useState(false);
  const { tyreStorageOrdersByClientId, loadingTyreStorageOrders } = orderStore;
  const tyreStorageOrders = toJS(orderStore.tyreStorageOrders);
  const currentTyreStorageOrder = toJS(orderStore.currentTyreStorageOrder);

  useEffect(() => {
    if ((orderId
           && (tyreStorageOrdersByClientId === clientId)
           && (currentTyreStorageOrder?.id !== orderId)
           && (!tyreStorageOrders.find((v) => v.id === orderId))
           && (!loadingTyreStorageOrders))
        || (orderId // для случая, когда указан только orderId и не указан clientId
          && !clientId
          && (currentTyreStorageOrder?.id !== orderId)
        )) {
      try {
        setLoading(true);
        orderActions.getTyreInStorageByOrderId(orderId);
      } finally {
        setLoading(false);
      }
    }
  }, [tyreStorageOrders, orderId, loadingTyreStorageOrders, currentTyreStorageOrder, tyreStorageOrdersByClientId]);

  // при очистке выбора сбрасываем догруженный по orderId комплект,
  // чтобы он не оставался единственной опцией в дропдауне
  useEffect(() => {
    if (!orderId && currentTyreStorageOrder) {
      orderActions.clearCurrentTyreStorageOrder();
    }
  }, [orderId, currentTyreStorageOrder]);

  const loadDate = async () => {
    await orderActions.clearTyreStorageOrders();
    await orderActions.getTyreInStorageOrders(clientId);
  };

  useEffect(() => {
    if (clientId && (clientId !== tyreStorageOrdersByClientId)) {
      try {
        setLoading(true);
        loadDate();
      } finally {
        setLoading(false);
      }
    }
  }, [clientId, notActive, tyreStorageOrdersByClientId]);

  // Комплект, вбитый в запись, может уже не быть в by_client-списке (например, выдан) —
  // его догружаем по orderId и добавляем к списку, а не подменяем список одной заявкой,
  // иначе сменить комплект в записи невозможно
  const tyreStorageArray = () => {
    if (!currentTyreStorageOrder || tyreStorageOrders.some((v) => v.id === currentTyreStorageOrder.id)) {
      return tyreStorageOrders;
    }
    return [...tyreStorageOrders, currentTyreStorageOrder];
  };

  const tyreStorageOrdersConverted = tyreStorageArray().map((v) => {
    return {
      id: v.id,
      name: tyreStorageOrderOptionName(v),
    };
  });

  return (
    multiSelect
      ? (
        <MultiSelectInput
          { ...rest }
          isMulti
          noOptionsMessage={ () => { return 'Нет вариантов'; } }
          data={ tyreStorageOrdersConverted }
          dataTitle='name'
          dataValue='id'
          loading={ loading }
          sort
          name={ name }
          label={ label }
          placeholder = { placeholder }          
          // disabled={ notActive || (!clientId) || (orderId && (!tyreStorageOrders.find((v) => v.id === orderId))) } // отключаем, если выбран по orderId комплект
        />
      )
      : (
        <SelectInput
          { ...rest }
          data={ tyreStorageOrdersConverted }
          dataTitle='name'
          dataValue='id'
          loading={ loading }
          sort
          name={ name }
          // disabled={ disabled }
          label={ label }
          placeholder = { placeholder }          
          // disabled={ notActive || (!clientId) || (orderId && (!tyreStorageOrders.find((v) => v.id === orderId))) } // отключаем, если выбран по orderId комплект
        />
      )
  );
};

export default inject('orderStore')(observer(TyreStorageOrdersSelector));
